// Add this line to silence ESLint for google global variable /* global google
*/ // Triple-slash reference for google.maps types ///
<reference types="@types/google.maps" />

// Declare global variables declare global { interface Window { google: any; } }

<template>
  <div class="map-container">
    <!-- Add loading indicator -->
    <div v-if="isLocating" class="location-loading">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t("map.loading") }}</span>
      </div>
      <div class="loading-text">{{ $t("map.findingLocation") }}</div>
    </div>
    <div id="map" ref="mapRef" class="map-area"></div>

    <!-- Search Box -->
    <div class="search-box">
      <div class="input-group">
        <input
          type="text"
          ref="searchInput"
          class="form-control search-input"
          :placeholder="$t('map.searchAddress')"
        />
        <button
          class="btn btn-outline-secondary"
          type="button"
          @click="clearSearch"
        >
          <i class="bi bi-x"></i>
        </button>
      </div>
    </div>

    <!-- Drawing Mode Controls -->
    <div class="drawing-controls">
      <button
        @click="setDrawingMode('move')"
        :class="['control-button', { active: currentMode === 'move' }]"
        :title="$t('map.moveMap')"
      >
        <i class="bi bi-arrows-move"></i>
      </button>
      <button
        @click="captureMapScreenshot"
        class="control-button"
        :title="$t('map.takeScreenshot')"
      >
        <i class="bi bi-camera"></i>
      </button>
      <button
        @click="findMyLocation"
        class="control-button"
        :title="$t('map.findMyLocation')"
      >
        <i class="bi bi-crosshair"></i>
      </button>
      <button
        @click="clearMap"
        class="control-button"
        :title="$t('map.clearMap')"
      >
        <i class="bi bi-arrow-repeat"></i>
      </button>

      <button
        @click="setDrawingMode('delete')"
        :class="['control-button', { active: currentMode === 'delete' }]"
        :disabled="areas.length === 0"
        :title="$t('map.deleteShape')"
        class="hidden-button"
      >
        <i class="bi bi-trash"></i>
      </button>
      <button
        @click="setDrawingMode('roof')"
        :class="['control-button', { active: currentMode === 'roof' }]"
        :title="$t('map.drawRoof')"
        class="hidden-button"
      >
        <i class="bi bi-house"></i>
      </button>
      <button
        @click="setDrawingMode('obstacle')"
        :class="['control-button', { active: currentMode === 'obstacle' }]"
        :title="$t('map.drawObstacle')"
        class="hidden-button"
      >
        <i class="bi bi-slash-circle"></i>
      </button>
      <button
        @click="setDrawingMode('slope')"
        :class="['control-button', { active: currentMode === 'slope' }]"
        :disabled="areas.length === 0"
        :title="$t('map.insertSlope')"
        class="hidden-button"
      >
        <i class="bi bi-building"></i>
      </button>
      <button
        @click="setDrawingMode('addPanel')"
        :class="['control-button', { active: currentMode === 'addPanel' }]"
        :disabled="areas.length === 0"
        :title="$t('map.addSolarPanel')"
        class="hidden-button"
      >
        <i class="bi bi-grid"></i>
      </button>
      <button
        @click="setDrawingMode('movePanel')"
        :class="['control-button', { active: currentMode === 'movePanel' }]"
        :disabled="areas.length === 0 || panels.length === 0"
        :title="$t('map.movePanel')"
        class="hidden-button"
      >
        <i class="bi bi-arrows-move"></i>
      </button>
    </div>

    <!-- Screenshot Notification -->
    <div v-if="showScreenshotNotification" class="screenshot-notification">
      <div class="alert alert-success">
        <i class="bi bi-check-circle me-2"></i>
        {{ $t("map.screenshotSuccess") }}
      </div>
    </div>

    <!-- Screenshot Info Popup -->
    <div
      v-if="showScreenshotInfo && screenshotInfo"
      class="screenshot-info-popup"
    >
      <div class="screenshot-info-content">
        <div class="screenshot-header">
          <h5>{{ $t("map.screenshotTitle") }}</h5>
          <button class="btn-close" @click="closeScreenshotInfo"></button>
        </div>

        <div class="screenshot-image-container">
          <img
            :src="screenshotInfo.imageUrl"
            :alt="$t('map.screenshot.title')"
          />
        </div>
      </div>
    </div>

    <!-- Slope Input Popup -->
    <div v-if="showSlopePopup" class="slope-popup">
      <div class="slope-popup-content">
        <h6>{{ $t("map.slope.title") }}</h6>
        <div class="input-group mb-3">
          <input
            type="number"
            v-model.number="slopeValue"
            min="0"
            max="90"
            class="form-control"
            :placeholder="$t('map.slope.enterSlope')"
          />
          <span class="input-group-text">°</span>
        </div>

        <h6>{{ $t("map.slope.direction") }}</h6>
        <div class="direction-indicator mb-3">
          <div class="compass-rose">
            <div
              class="compass-arrow"
              :style="{ transform: `rotate(${directionValue}deg)` }"
            ></div>
            <div class="compass-label north">N</div>
            <div class="compass-label east">E</div>
            <div class="compass-label south">S</div>
            <div class="compass-label west">W</div>
          </div>
          <div class="direction-value">
            <div class="input-group">
              <input
                type="number"
                v-model.number="directionValue"
                min="0"
                max="359"
                class="form-control"
                :placeholder="$t('map.slope.enterDirection')"
              />
              <span class="input-group-text">°</span>
            </div>
            <small class="text-muted">{{
              getDirectionName(directionValue)
            }}</small>
          </div>
        </div>

        <div
          v-if="slopeStage === 'select-baseline'"
          class="alert alert-info mb-3"
        >
          <i class="bi bi-info-circle me-2"></i>
          {{ $t("map.slope.selectRidgeLine") }}
        </div>

        <div class="d-flex justify-content-end">
          <button class="btn btn-outline-secondary me-2" @click="cancelSlope">
            {{ $t("map.slope.cancel") }}
          </button>
          <button
            class="btn btn-primary"
            :disabled="!isValidSlope || !isValidDirection"
            @click="applySlope"
          >
            {{
              slopeStage === "input"
                ? $t("map.slope.selectRidge")
                : $t("map.slope.apply")
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* eslint-disable */
// Add this line to silence ESLint for google global variable
/* global google */

import {
  defineComponent,
  ref,
  onMounted,
  watch,
  computed,
  onUnmounted,
} from "vue";
import { useMapStateStore } from "@/stores/mapState";
import { useProjectStore } from "@/stores/project";
import { useI18n } from "vue-i18n";
import html2canvas from "html2canvas";

// Declare window with google property to satisfy TypeScript
declare global {
  interface Window {
    google: any;
  }
}

// Interface for GoogleDrawingEvent
interface GoogleDrawingEvent {
  type: string;
  overlay: any;
}

// Interface for GeoPoint
interface GeoPoint {
  latitude: number;
  longitude: number;
}

// Interface for Area
interface Area {
  id: number;
  points: GeoPoint[];
  area_sqm: number;
  roof_slope: number;
  roof_direction?: number; // Direction in degrees (0-359)
  is_obstacle?: boolean;
  ridge_line?: {
    // Ridge line points defining roof direction
    start: GeoPoint;
    end: GeoPoint;
  };
}

// Interface for panel
interface Panel {
  id: number;
  position: GeoPoint; // Center position of the panel
  direction: number; // Direction in degrees (0-359)
  width: number; // Width in meters (default 1m)
  height: number; // Height in meters (default 2m)
  roofId: number; // ID of the roof this panel is on
}

interface RoofArea extends Area {
  is_obstacle: boolean;
}

// Interface for area info
interface AreaInfo {
  totalArea: string;
  usableArea: string;
}

// Interface for ExtendedPolygon to fix TypeScript errors with Google Maps polygons
interface ExtendedPolygon extends google.maps.Polygon {
  id?: number;
  is_obstacle?: boolean;
}

// Define EdgeType interface for better TypeScript support
interface EdgeType {
  start: GeoPoint;
  end: GeoPoint;
}

interface ScreenshotInfo {
  imageUrl: string;
  dimensions: {
    width: number;
    height: number;
  };
  corners: {
    northwest: { lat: number; lng: number };
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
    southeast: { lat: number; lng: number };
  };
}

export default defineComponent({
  name: "MapComponent",
  setup() {
    const mapStateStore = useMapStateStore();
    const projectStore = useProjectStore();
    const mapRef = ref<HTMLElement | null>(null);
    const searchInput = ref<HTMLInputElement | null>(null);
    const searchBox = ref<any>(null);
    const map = ref<any>(null);
    const drawingManager = ref<any>(null);
    const currentMode = ref<
      | "move"
      | "roof"
      | "obstacle"
      | "delete"
      | "slope"
      | "addPanel"
      | "movePanel"
    >("move");
    const debug = ref<boolean>(true);
    const areas = ref<RoofArea[]>([]);
    const panels = ref<Panel[]>([]);
    const activePanelMarker = ref<google.maps.Polygon | null>(null);
    const selectedPanel = ref<Panel | null>(null);
    const showScreenshotNotification = ref<boolean>(false);
    const isLocating = ref<boolean>(false);

    // Slope related state
    const showSlopePopup = ref<boolean>(false);
    const slopeValue = ref<number>(0);
    const directionValue = ref<number>(180); // Default to South (optimal in Northern Hemisphere)
    const selectedShape = ref<ExtendedPolygon | null>(null);
    const slopeStage = ref<"input" | "select-baseline">("input");
    const temporaryMarkers = ref<any[]>([]);
    const hoveredLine = ref<google.maps.Polyline | null>(null);

    // Screenshot related state
    const showScreenshotInfo = ref<boolean>(false);
    const screenshotInfo = ref<ScreenshotInfo | null>(null);

    const primaryColor = ref("#2196f3");
    const secondaryColor = ref("#1976d2");
    const redColor = ref("#FF0000");
    const greenColor = ref("#4CAF50");

    // Check if the slope value is valid
    const isValidSlope = computed(() => {
      const value = slopeValue.value;
      return value !== null && value >= 0 && value <= 90;
    });

    // Check if the direction value is valid
    const isValidDirection = computed(() => {
      const value = directionValue.value;
      return value !== null && value >= 0 && value <= 359;
    });

    // Get direction name from degrees
    const getDirectionName = (degrees: number): string => {
      const directions = [
        "North",
        "North-Northeast",
        "Northeast",
        "East-Northeast",
        "East",
        "East-Southeast",
        "Southeast",
        "South-Southeast",
        "South",
        "South-Southwest",
        "Southwest",
        "West-Southwest",
        "West",
        "West-Northwest",
        "Northwest",
        "North-Northwest",
      ];
      // Convert degrees to 16-point compass index
      const index = Math.round(degrees / 22.5) % 16;
      return directions[index];
    };

    // Calculate the distance between two points
    const calculateDistance = (p1: GeoPoint, p2: GeoPoint): number => {
      const latDiff = p1.latitude - p2.latitude;
      const lngDiff = p1.longitude - p2.longitude;
      return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);
    };

    // Calculate the angle between two points in degrees (0-359)
    const calculateAngle = (p1: GeoPoint, p2: GeoPoint): number => {
      const dx = p2.longitude - p1.longitude;
      const dy = p2.latitude - p1.latitude;

      // Calculate angle in radians, then convert to degrees
      let angle = Math.atan2(dy, dx) * (180 / Math.PI);

      // Convert to 0-359 range
      if (angle < 0) angle += 360;

      return angle;
    };

    // Detect the roof direction from polygon shape
    const detectRoofDirection = (shape: ExtendedPolygon): number => {
      const points = polygonToGeoPoints(shape);
      if (points.length < 3) return 180; // Default to South

      let maxDistance = 0;
      let longestSideIndex = 0;

      // Find the longest side of the polygon
      for (let i = 0; i < points.length; i++) {
        const nextIndex = (i + 1) % points.length;
        const distance = calculateDistance(points[i], points[nextIndex]);

        if (distance > maxDistance) {
          maxDistance = distance;
          longestSideIndex = i;
        }
      }

      // Get the points of the longest side
      const p1 = points[longestSideIndex];
      const p2 = points[(longestSideIndex + 1) % points.length];

      // Calculate the angle of the longest side
      let angle = calculateAngle(p1, p2);

      // Direction is perpendicular to the longest side
      angle = (angle + 90) % 360;

      logDebug("Detected roof direction:", angle);
      return Math.round(angle);
    };

    // Update handleOverlayComplete
    const handleOverlayComplete = (event: GoogleDrawingEvent) => {
      if (!map.value) return;

      const overlay = event.overlay;
      if (event.type !== google.maps.drawing.OverlayType.POLYGON) return;

      const newShape = overlay as ExtendedPolygon;
      const shapeId = Date.now();

      // Store the ID on the shape object for future reference
      newShape.id = shapeId;
      newShape.is_obstacle = currentMode.value === "obstacle";

      // Make the shape clickable
      newShape.setOptions({
        clickable: true,
        editable: true,
        fillColor:
          currentMode.value === "obstacle" ? redColor.value : greenColor.value,
        strokeColor:
          currentMode.value === "obstacle" ? redColor.value : greenColor.value,
      });

      // Auto-detect direction for roof shapes
      const direction = newShape.is_obstacle
        ? undefined
        : detectRoofDirection(newShape);

      const areaData: RoofArea = {
        id: shapeId,
        points: polygonToGeoPoints(newShape),
        area_sqm: calculateAreaInSqMeters(newShape),
        roof_slope: 0,
        roof_direction: direction,
        is_obstacle: currentMode.value === "obstacle",
      };

      // Add to local areas array
      areas.value.push(areaData);

      logDebug("New shape added:", areaData);

      // Add click listener directly to the shape
      google.maps.event.addListener(newShape, "click", () => {
        if (currentMode.value === "delete") {
          handleShapeClick(newShape);
        } else if (currentMode.value === "slope") {
          handleSlopeClick(newShape);
        }
      });

      // Add path change listener to update area when shape is modified
      const path = newShape.getPath();
      google.maps.event.addListener(path, "set_at", () => {
        updateShapeArea(newShape);
      });
      google.maps.event.addListener(path, "insert_at", () => {
        updateShapeArea(newShape);
      });
      google.maps.event.addListener(path, "remove_at", () => {
        updateShapeArea(newShape);
      });

      // Add to map's overlays array to track this shape
      if (!map.value.overlays) {
        map.value.overlays = [];
      }
      map.value.overlays.push(newShape);
    };

    // Computed property for area information
    const areaInfo = computed<AreaInfo>(() => {
      if (!areas.value) {
        return {
          totalArea: "0 m²",
          usableArea: "0 m²",
        };
      }

      // Calculate total roof area
      const totalArea = areas.value.reduce((sum, area) => {
        if (area.is_obstacle) return sum;
        return sum + area.area_sqm;
      }, 0);

      // Calculate obstacle area
      const obstacleArea = areas.value.reduce((sum, area) => {
        if (!area.is_obstacle) return sum;
        return sum + area.area_sqm;
      }, 0);

      // Calculate usable area (total - obstacles)
      const usableArea = totalArea - obstacleArea;

      return {
        totalArea: `${totalArea.toFixed(2)} m²`,
        usableArea: `${usableArea.toFixed(2)} m²`,
      };
    });

    // Debug logger
    const logDebug = (message: string, data?: any) => {
      if (debug.value) {
        console.log(`[MapComponent Debug] ${message}`, data || "");
      }
    };

    const loadGoogleMapsScript = async () => {
      try {
        const { loadGoogleMapsAPI } = await import("@/utils/googleMapsLoader");
        await loadGoogleMapsAPI();
      } catch (error) {
        throw new Error(`Failed to load Google Maps API: ${error}`);
      }
    };

    // Initialize search box
    const initSearchBox = () => {
      if (!searchInput.value || !map.value) return;

      searchBox.value = new google.maps.places.Autocomplete(searchInput.value, {
        types: ["address"],
      });

      searchBox.value.bindTo("bounds", map.value);

      searchBox.value.addListener("place_changed", () => {
        const place = searchBox.value.getPlace();

        if (!place.geometry || !place.geometry.location) {
          alert("No details available for this place");
          return;
        }

        // Always navigate to exact location with maximum zoom
        map.value.setCenter(place.geometry.location);
        map.value.setZoom(21); // Maximum zoom level for Google Maps

        logDebug("Moved to searched location with maximum zoom", place);
      });
    };

    // Clear the search box
    const clearSearch = () => {
      if (searchInput.value) {
        searchInput.value.value = "";
      }
    };

    // Helper function to convert polygon to GeoPoints
    const polygonToGeoPoints = (polygon: any): GeoPoint[] => {
      const path = polygon.getPath().getArray();
      return path.map((point: any) => ({
        latitude: point.lat(),
        longitude: point.lng(),
      }));
    };

    // Helper function to calculate area in square meters
    const calculateAreaInSqMeters = (polygon: any): number => {
      return google.maps.geometry.spherical.computeArea(
        polygon.getPath().getArray()
      );
    };

    // Find user's current location
    const findMyLocation = () => {
      if (!map.value) return;

      if (navigator.geolocation) {
        isLocating.value = true; // Start loading

        // Check if we're in a secure context
        const isSecureContext = window.isSecureContext;
        const isLocalhost =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";

        if (!isSecureContext && !isLocalhost) {
          alert(
            "Geolocation requires a secure context (HTTPS) or localhost. Please use HTTPS or run on localhost for development."
          );
          isLocating.value = false;
          return;
        }

        // Request location with high accuracy
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            map.value?.setCenter(pos);
            map.value?.setZoom(20);

            logDebug("Found user location:", pos);
            isLocating.value = false; // End loading
          },
          (error) => {
            isLocating.value = false; // End loading on error

            // Handle different error cases
            switch (error.code) {
              case error.PERMISSION_DENIED:
                alert(
                  "Location access was denied. Please allow location access in your browser settings."
                );
                break;
              case error.POSITION_UNAVAILABLE:
                alert("Location information is unavailable. Please try again.");
                break;
              case error.TIMEOUT:
                alert("Location request timed out. Please try again.");
                break;
              default:
                alert("An unknown error occurred while getting your location.");
                break;
            }

            logDebug("Error getting user location:", error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );
      } else {
        alert("Geolocation is not supported by your browser.");
        logDebug("Geolocation is not supported by this browser.");
      }
    };

    // Reset the map and clear all shapes
    const clearMap = () => {
      logDebug("Clearing map");

      // Store current position and zoom before rebuilding
      const currentCenter = mapStateStore.center;
      const currentZoom = mapStateStore.zoom;

      // Clear any ridge lines and temporary markers
      clearRidgeSelection();

      // Reset slope-related state
      showSlopePopup.value = false;
      selectedShape.value = null;
      slopeStage.value = "input";

      // Clear panels array
      panels.value = [];

      // Clear all shapes from the map
      if (map.value) {
        logDebug("Cleaning up map and all shapes");

        // Remove all shapes
        if (map.value.overlays) {
          map.value.overlays.forEach((shape: any) => {
            if (shape) {
              // First remove all event listeners
              google.maps.event.clearInstanceListeners(shape);

              // Also clear path listeners
              if (shape.getPath) {
                const path = shape.getPath();
                if (path) {
                  google.maps.event.clearInstanceListeners(path);
                }
              }

              // Then remove from map
              shape.setMap(null);
            }
          });
          map.value.overlays = [];
        }

        // Clear all map event listeners
        google.maps.event.clearInstanceListeners(map.value);
        map.value = null;
      }

      // Clear local areas array
      areas.value = [];

      // Remove any custom cursors
      mapRef.value?.classList.remove("panel-cursor", "move-cursor");

      // 3. Re-initialize the map and drawing manager
      initMap();

      // 4. Restore previous center and zoom
      if (map.value) {
        map.value.setCenter(currentCenter);
        map.value.setZoom(currentZoom);
        mapStateStore.updateMapState(currentCenter, currentZoom);
      }

      // 5. Set mode back to move
      currentMode.value = "move";

      logDebug("Map has been cleared and reset to previous position");
    };

    // Initialize map
    const initMap = () => {
      if (!mapRef.value || !window.google) {
        logDebug("Failed to initialize map - missing dependencies");
        return;
      }

      logDebug("Initializing map");
      map.value = new google.maps.Map(mapRef.value, {
        center: mapStateStore.center,
        zoom: mapStateStore.zoom,
        mapTypeId: "hybrid",
        mapTypeControl: false,
        fullscreenControl: false,
        tilt: 0,
      });

      // Initialize our shapes tracking array
      map.value.overlays = [];

      // Add listener for center and zoom changes
      map.value.addListener("idle", () => {
        if (map.value) {
          const center = map.value.getCenter()?.toJSON();
          const zoom = map.value.getZoom();
          if (center && zoom) {
            mapStateStore.updateMapState(center, zoom);
            logDebug("Map state updated:", { center, zoom });
          }
        }
      });

      // Add zoom event listener to ensure shapes stay consistent
      map.value.addListener("zoom_changed", () => {
        // This helps ensure shapes are displayed correctly after zooming
        if (map.value && map.value.overlays) {
          logDebug("Zoom changed, checking shapes consistency");
          // Verify all visible shapes match what's in our store
          const storeIds = areas.value.map((a) => a.id) || [];
          const mapIds = map.value.overlays
            .map((s: any) => s?.id)
            .filter(Boolean);

          // Check if any shapes on the map should be removed
          map.value.overlays.forEach((shape: any) => {
            if (shape && shape.id && !storeIds.includes(shape.id)) {
              logDebug(`Found orphaned shape: ${shape.id}, removing it`);
              google.maps.event.clearInstanceListeners(shape);
              shape.setMap(null);
            }
          });

          // Filter out any null or removed shapes
          map.value.overlays = map.value.overlays.filter(
            (shape: any) => shape && shape.getMap && shape.getMap() !== null
          );
        }
      });

      drawingManager.value = new google.maps.drawing.DrawingManager({
        drawingControl: false,
        drawingMode: null,
        polygonOptions: {
          fillColor: greenColor.value,
          fillOpacity: 0.3,
          strokeWeight: 2,
          strokeColor: greenColor.value,
          clickable: true,
          editable: false,
        },
      });

      drawingManager.value.setMap(map.value);

      google.maps.event.addListener(
        drawingManager.value,
        "overlaycomplete",
        handleOverlayComplete
      );

      initSearchBox();

      // Add click listener for panel placement
      map.value.addListener("click", handleMapClickForPanel);

      // Check if projectStore has valid mapState
      if (mapRef.value) {
        const style = getComputedStyle(mapRef.value);
        primaryColor.value =
          style.getPropertyValue("--primary-color").trim() || "#2196f3";
        secondaryColor.value =
          style.getPropertyValue("--secondary-color").trim() || "#1976d2";
        redColor.value =
          style.getPropertyValue("--color-red").trim() || "#FF0000";
        greenColor.value =
          style.getPropertyValue("--color-green").trim() || "#4CAF50";
      }

      if (projectStore.project?.map_state) {
        const { center, zoom } = projectStore.project.map_state;
        // Check if center exists and has valid coordinates (not 0,0)
        if (center && center.lat !== 0 && center.lng !== 0) {
          mapStateStore.updateMapState(center, zoom);
          logDebug("Using map state from project store:", { center, zoom });
        }
      }

      logDebug("Map initialized successfully");
    };

    // Clear ridge selection markers
    const clearRidgeSelection = () => {
      // Remove any temporary markers
      temporaryMarkers.value.forEach((marker) => {
        if (marker) marker.setMap(null);
      });
      temporaryMarkers.value = [];

      // Remove hovered line
      if (hoveredLine.value) {
        hoveredLine.value.setMap(null);
        hoveredLine.value = null;
      }
    };

    // Add click handler for shapes
    const handleShapeClick = (shape: ExtendedPolygon) => {
      if (currentMode.value === "delete") {
        logDebug("Attempting to delete shape with ID:", shape.id);

        // Find the area with matching ID
        const areaIndex = areas.value.findIndex((area) => area.id === shape.id);

        if (areaIndex !== -1) {
          // Remove the shape from the map
          shape.setMap(null);

          // Remove from overlays array
          if (map.value && map.value.overlays) {
            const shapeIndex = map.value.overlays.indexOf(shape);
            if (shapeIndex !== -1) {
              map.value.overlays.splice(shapeIndex, 1);
            }
          }

          // Remove any ridge line visualizations associated with this shape
          clearRidgeSelection();

          // Remove the area from our areas array
          areas.value.splice(areaIndex, 1);
          logDebug("Shape deleted, areas remaining:", areas.value.length);
        } else {
          logDebug("Could not find area to delete");
        }
      }
    };

    // Function to update shape area when modified
    const updateShapeArea = (shape: ExtendedPolygon) => {
      if (!shape.id) return;

      // Find the area with matching ID
      const areaIndex = areas.value.findIndex((area) => area.id === shape.id);
      if (areaIndex === -1) return;

      // Update points and area_sqm
      const updatedArea = areas.value[areaIndex];
      updatedArea.points = polygonToGeoPoints(shape);
      updatedArea.area_sqm = calculateAreaInSqMeters(shape);

      // Replace in array to trigger reactivity
      areas.value.splice(areaIndex, 1, updatedArea);

      logDebug("Shape updated:", updatedArea);
    };

    // Handle slope click
    const handleSlopeClick = (shape: ExtendedPolygon) => {
      // Only apply to roof areas, not obstacles
      if (shape.is_obstacle) {
        logDebug("Cannot apply slope to obstacles");
        return;
      }

      selectedShape.value = shape;
      showSlopePopup.value = true;
      slopeStage.value = "input";

      // Get the current slope value if already set
      const areaIndex = areas.value.findIndex((area) => area.id === shape.id);
      if (areaIndex !== -1) {
        slopeValue.value = areas.value[areaIndex].roof_slope;
        directionValue.value =
          areas.value[areaIndex].roof_direction || detectRoofDirection(shape);

        // If a ridge line is saved, visualize it
        if (areas.value[areaIndex].ridge_line) {
          const ridgeLine = areas.value[areaIndex].ridge_line;
          if (ridgeLine && ridgeLine.start && ridgeLine.end) {
            const ridgeLineVisual = new google.maps.Polyline({
              path: [
                {
                  lat: ridgeLine.start.latitude,
                  lng: ridgeLine.start.longitude,
                },
                { lat: ridgeLine.end.latitude, lng: ridgeLine.end.longitude },
              ],
              map: map.value,
              strokeColor: secondaryColor.value,
              strokeWeight: 4,
              strokeOpacity: 1.0,
              zIndex: 99,
            });

            // Store this temporary marker to remove when done
            temporaryMarkers.value.push(ridgeLineVisual);

            logDebug("Restored ridge line visualization");
          }
        }
      } else {
        slopeValue.value = 0;
        directionValue.value = detectRoofDirection(shape);
      }

      logDebug("Opening slope popup for shape:", shape.id);
    };

    // Cancel slope operation
    const cancelSlope = () => {
      showSlopePopup.value = false;
      selectedShape.value = null;
      slopeStage.value = "input";
      clearRidgeSelection();
      logDebug("Slope operation cancelled");
    };

    // Setup edge detection and highlighting
    const setupEdgeHighlighting = () => {
      if (!map.value || !selectedShape.value) return;

      // Clear previous selections
      clearRidgeSelection();

      // Temporarily disable shape editing
      selectedShape.value.setEditable(false);

      // Create the hover line (initially invisible)
      hoveredLine.value = new google.maps.Polyline({
        map: map.value,
        strokeColor: secondaryColor.value,
        strokeWeight: 5,
        strokeOpacity: 0.8,
        visible: false,
        zIndex: 100,
      });

      // Convert shape to edges for hover detection
      const edges = getShapeEdges(selectedShape.value);
      logDebug("Shape edges for highlighting:", edges.length);

      // Add mousemove listener to detect hovering over edges
      const mouseMoveListener = map.value.addListener(
        "mousemove",
        (event: google.maps.MapMouseEvent) => {
          if (!event.latLng) return;

          const mousePoint = {
            latitude: event.latLng.lat(),
            longitude: event.latLng.lng(),
          };

          // Find the closest edge to the mouse position
          const { closestEdge, distance } = findClosestEdge(mousePoint, edges);

          // If mouse is close enough to an edge, highlight it
          if (closestEdge && distance < 0.00005) {
            // Threshold for detecting hover
            const highlightPath = [
              {
                lat: closestEdge.start.latitude,
                lng: closestEdge.start.longitude,
              },
              { lat: closestEdge.end.latitude, lng: closestEdge.end.longitude },
            ];
            hoveredLine.value!.setPath(highlightPath);
            // Set visible using setOptions instead of setVisible
            hoveredLine.value!.setOptions({ visible: true });
          } else {
            // Set invisible using setOptions instead of setVisible
            hoveredLine.value!.setOptions({ visible: false });
          }
        }
      );

      // Add click listener to select the hovered edge
      const clickListener = map.value.addListener(
        "click",
        (event: google.maps.MapMouseEvent) => {
          if (!event.latLng || !hoveredLine.value) return;

          // Check if the hover line is visible
          let isVisible = false;
          try {
            // Try to access visible property via getOptions()
            const options = (hoveredLine.value as any).getOptions();
            isVisible = options && options.visible;
          } catch (e) {
            // Fall back to checking if the line has points
            isVisible = hoveredLine.value.getPath().getLength() > 0;
          }

          if (!isVisible) return;

          // Get the path of the currently highlighted line
          const path = hoveredLine.value.getPath();
          if (path.getLength() !== 2) return;

          const start = path.getAt(0);
          const end = path.getAt(1);

          // Convert to GeoPoints
          const startPoint: GeoPoint = {
            latitude: start.lat(),
            longitude: start.lng(),
          };
          const endPoint: GeoPoint = {
            latitude: end.lat(),
            longitude: end.lng(),
          };

          // Calculate the direction based on the selected ridge line
          const angle = calculateAngle(startPoint, endPoint);
          // Direction is perpendicular to the ridge line
          const direction = (angle + 90) % 360;

          // Update direction value
          directionValue.value = Math.round(direction);

          // Clear any previous ridge line visualizations
          temporaryMarkers.value.forEach((marker) => {
            if (marker) marker.setMap(null);
          });
          temporaryMarkers.value = [];

          // Create permanent visual indicators for the selected ridge
          const ridgeLineVisual = new google.maps.Polyline({
            path: [start, end],
            map: map.value,
            strokeColor: secondaryColor.value,
            strokeWeight: 4,
            strokeOpacity: 1.0,
            zIndex: 99,
          });

          // Save visual elements
          temporaryMarkers.value.push(ridgeLineVisual);

          // Remove hover effect
          hoveredLine.value.setOptions({ visible: false });

          // Remove listeners
          google.maps.event.removeListener(mouseMoveListener);
          google.maps.event.removeListener(clickListener);

          // Re-enable shape editing
          selectedShape.value!.setEditable(true);

          logDebug("Ridge line selected, direction set to:", direction);
        }
      );

      return [mouseMoveListener, clickListener];
    };

    // Get all edges from a polygon shape
    const getShapeEdges = (shape: ExtendedPolygon): EdgeType[] => {
      const points = polygonToGeoPoints(shape);
      const edges: EdgeType[] = [];

      for (let i = 0; i < points.length; i++) {
        const nextIndex = (i + 1) % points.length;
        edges.push({
          start: points[i],
          end: points[nextIndex],
        });
      }

      return edges;
    };

    // Find the closest edge to a point
    const findClosestEdge = (
      point: GeoPoint,
      edges: EdgeType[]
    ): { closestEdge: EdgeType | null; distance: number } => {
      let closestEdge: EdgeType | null = null;
      let minDistance = Infinity;

      edges.forEach((edge) => {
        const distance = distanceToLine(point, edge.start, edge.end);
        if (distance < minDistance) {
          minDistance = distance;
          closestEdge = edge;
        }
      });

      return { closestEdge, distance: minDistance };
    };

    // Calculate distance from a point to a line segment
    const distanceToLine = (
      point: GeoPoint,
      lineStart: GeoPoint,
      lineEnd: GeoPoint
    ) => {
      // Convert to Cartesian for simplicity
      const p = [point.longitude, point.latitude];
      const v = [lineStart.longitude, lineStart.latitude];
      const w = [lineEnd.longitude, lineEnd.latitude];

      // Return minimum distance between line segment vw and point p
      const l2 = Math.pow(w[0] - v[0], 2) + Math.pow(w[1] - v[1], 2); // length squared of line segment

      // If segment is just a point, return distance to the point
      if (l2 === 0)
        return Math.sqrt(Math.pow(p[0] - v[0], 2) + Math.pow(p[1] - v[1], 2));

      // Consider the line extending the segment, parameterized as v + t (w - v)
      // We find projection of point p onto the line.
      // It falls where t = [(p-v) . (w-v)] / |w-v|^2
      // We clamp t from [0,1] to handle points outside the segment vw.
      const t = Math.max(
        0,
        Math.min(
          1,
          ((p[0] - v[0]) * (w[0] - v[0]) + (p[1] - v[1]) * (w[1] - v[1])) / l2
        )
      );

      const projection = [v[0] + t * (w[0] - v[0]), v[1] + t * (w[1] - v[1])];

      return Math.sqrt(
        Math.pow(p[0] - projection[0], 2) + Math.pow(p[1] - projection[1], 2)
      );
    };

    // Apply slope value
    const applySlope = () => {
      if (
        !selectedShape.value ||
        !isValidSlope.value ||
        !isValidDirection.value
      )
        return;

      if (slopeStage.value === "input") {
        // Move to selecting ridge line stage
        slopeStage.value = "select-baseline";
        const listeners = setupEdgeHighlighting();
        logDebug("Ready to select ridge line");
      } else {
        // Apply the slope
        const shapeId = selectedShape.value.id;
        if (!shapeId) return;

        const areaIndex = areas.value.findIndex((area) => area.id === shapeId);
        if (areaIndex !== -1) {
          // Update the slope and direction values
          const updatedArea = areas.value[areaIndex];
          updatedArea.roof_slope = slopeValue.value;
          updatedArea.roof_direction = directionValue.value;

          // Save the ridge line if it was selected
          if (temporaryMarkers.value.length > 0) {
            // Find the ridge line points from the visual markers
            const polylines = temporaryMarkers.value.filter(
              (marker) => marker instanceof google.maps.Polyline
            );

            if (polylines.length > 0) {
              const path = polylines[0].getPath();
              if (path && path.getLength() === 2) {
                const start = path.getAt(0);
                const end = path.getAt(1);

                updatedArea.ridge_line = {
                  start: {
                    latitude: start.lat(),
                    longitude: start.lng(),
                  },
                  end: {
                    latitude: end.lat(),
                    longitude: end.lng(),
                  },
                };

                logDebug(
                  "Ridge line saved with shape:",
                  updatedArea.ridge_line
                );
              }
            }
          }

          // Replace in array to trigger reactivity
          areas.value.splice(areaIndex, 1, updatedArea);

          logDebug("Slope and direction updated:", updatedArea);
        }

        // Close the popup and reset
        showSlopePopup.value = false;
        selectedShape.value = null;
        slopeStage.value = "input";
        clearRidgeSelection();
      }
    };

    // Check if a point is inside a polygon (alternative method)
    const isPointInPolygon = (
      point: google.maps.LatLng,
      polygon: google.maps.Polygon
    ): boolean => {
      // Manual implementation of point-in-polygon algorithm
      const vertices = polygon.getPath();
      let inside = false;

      // Ray casting algorithm
      for (
        let i = 0, j = vertices.getLength() - 1;
        i < vertices.getLength();
        j = i++
      ) {
        const xi = vertices.getAt(i).lng();
        const yi = vertices.getAt(i).lat();
        const xj = vertices.getAt(j).lng();
        const yj = vertices.getAt(j).lat();

        const intersect =
          yi > point.lat() !== yj > point.lat() &&
          point.lng() < ((xj - xi) * (point.lat() - yi)) / (yj - yi) + xi;

        if (intersect) inside = !inside;
      }

      return inside;
    };

    // Handle click on map to add panel
    const handleMapClickForPanel = (event: google.maps.MapMouseEvent) => {
      if (currentMode.value !== "addPanel" || !event.latLng) return;

      // Debug log to help diagnose issues
      logDebug("Panel placement clicked at:", {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });

      // SIMPLIFIED APPROACH: Skip roof containment check for now
      // Find any available roof area for the direction
      const nonObstacleAreas = areas.value.filter((area) => !area.is_obstacle);

      if (nonObstacleAreas.length === 0) {
        logDebug("No roof areas available");
        alert("Please create at least one roof area first.");
        return;
      }

      // Use the first roof's direction
      const containingRoof = nonObstacleAreas[0];
      const direction = containingRoof.roof_direction || 0;

      logDebug(
        `Using direction from roof: ${containingRoof.id}, direction: ${direction}`
      );

      // Create the panel at the click position with the chosen roof's direction
      createPanelShape(event.latLng, direction, containingRoof.id);
      logDebug("Panel added with simplified approach");
    };

    // Create a panel shape at the given position with the given direction
    const createPanelShape = (
      center: google.maps.LatLng,
      direction: number,
      roofId: number
    ): google.maps.Polygon => {
      logDebug(
        "Creating panel at position: " +
          center.toString() +
          " with direction: " +
          direction
      );

      // Panel dimensions in meters
      const panelWidth = 2; // 2 meters width
      const panelHeight = 2; // 2 meters height

      // Convert meters to approximate degrees at this latitude
      // At Earth's equator, 1 degree is approximately 111,000 meters
      // The conversion gets more complex at different latitudes
      const lat = center.lat();
      const metersPerDegreeLat = 111000; // approximate
      const metersPerDegreeLng = 111000 * Math.cos(lat * (Math.PI / 180));

      const widthDegrees = panelWidth / metersPerDegreeLng;
      const heightDegrees = panelHeight / metersPerDegreeLat;

      // Calculate the rotation angle in radians
      const directionRadians = (direction * Math.PI) / 180;

      // Create a rectangle with 90-degree corners
      // These are the corners if the panel wasn't rotated (facing north)
      const halfWidth = widthDegrees / 2;
      const halfHeight = heightDegrees / 2;

      // Define corners of unrotated rectangle
      const unrotatedCorners = [
        { x: -halfWidth, y: halfHeight }, // top-left
        { x: halfWidth, y: halfHeight }, // top-right
        { x: halfWidth, y: -halfHeight }, // bottom-right
        { x: -halfWidth, y: -halfHeight }, // bottom-left
      ];

      // Rotate each corner around the center
      const rotatedCorners = unrotatedCorners.map((corner) => {
        // Apply rotation matrix
        const rotatedX =
          corner.x * Math.cos(directionRadians) -
          corner.y * Math.sin(directionRadians);
        const rotatedY =
          corner.x * Math.sin(directionRadians) +
          corner.y * Math.cos(directionRadians);

        // Convert back to lat/lng and add to center
        return {
          lat: center.lat() + rotatedY,
          lng: center.lng() + rotatedX,
        };
      });

      // Create the panel as a Google Maps Polygon
      const panelPolygon = new google.maps.Polygon({
        paths: rotatedCorners,
        strokeColor: primaryColor.value, // Blue stroke
        strokeOpacity: 1.0,
        strokeWeight: 2,
        fillColor: primaryColor.value, // Blue fill
        fillOpacity: 0.5,
        map: map.value,
        zIndex: 3, // Above other shapes
        draggable: currentMode.value === "movePanel", // Only draggable in move mode
        clickable: true,
      });

      // Set panel ID and store in data
      const panelId = Date.now();
      (panelPolygon as any).panelId = panelId;

      // Store the panel in our data
      const newPanel: Panel = {
        id: panelId,
        position: {
          latitude: center.lat(),
          longitude: center.lng(),
        },
        direction: direction,
        width: panelWidth,
        height: panelHeight,
        roofId: roofId,
      };

      panels.value.push(newPanel);
      logDebug("Panel added to data array:", newPanel);

      // Add click handler for delete mode
      google.maps.event.addListener(panelPolygon, "click", () => {
        if (currentMode.value === "delete") {
          deletePanel(panelId);
        }
      });

      // Add drag events for move mode
      google.maps.event.addListener(panelPolygon, "dragstart", () => {
        if (currentMode.value !== "movePanel") return;
        logDebug("Panel drag started:", panelId);
      });

      google.maps.event.addListener(panelPolygon, "dragend", () => {
        if (currentMode.value !== "movePanel") return;

        // Get the new center of the panel
        const bounds = new google.maps.LatLngBounds();
        panelPolygon.getPath().forEach((point: google.maps.LatLng) => {
          bounds.extend(point);
        });

        const newCenter = bounds.getCenter();
        const newPosition: GeoPoint = {
          latitude: newCenter.lat(),
          longitude: newCenter.lng(),
        };

        // Update the panel position in our data
        const panelIndex = panels.value.findIndex((p) => p.id === panelId);
        if (panelIndex !== -1) {
          const updatedPanel = { ...panels.value[panelIndex] };
          updatedPanel.position = newPosition;
          panels.value.splice(panelIndex, 1, updatedPanel);
          logDebug("Panel position updated:", updatedPanel);
        }
      });

      // Add to map's overlays array to track this shape
      if (!map.value.overlays) {
        map.value.overlays = [];
      }
      map.value.overlays.push(panelPolygon);

      return panelPolygon;
    };

    // Delete a panel
    const deletePanel = (panelId: number) => {
      // Find the panel overlay
      if (map.value && map.value.overlays) {
        const panelIndex = map.value.overlays.findIndex(
          (overlay: any) => overlay.panelId === panelId
        );

        if (panelIndex !== -1) {
          // Remove from map
          const panelOverlay = map.value.overlays[panelIndex];
          panelOverlay.setMap(null);

          // Remove event listeners
          google.maps.event.clearInstanceListeners(panelOverlay);

          // Remove from overlays array
          map.value.overlays.splice(panelIndex, 1);
        }
      }

      // Remove from panels array
      const dataIndex = panels.value.findIndex((p) => p.id === panelId);
      if (dataIndex !== -1) {
        panels.value.splice(dataIndex, 1);
      }

      logDebug("Panel deleted:", panelId);
    };

    // Modify setDrawingMode to handle panel modes
    const setDrawingMode = (
      mode:
        | "move"
        | "roof"
        | "obstacle"
        | "delete"
        | "slope"
        | "addPanel"
        | "movePanel"
    ) => {
      // If we're switching modes, close any open popup and clear ridge selection
      if (currentMode.value !== mode) {
        if (showSlopePopup.value) {
          showSlopePopup.value = false;
          selectedShape.value = null;
          slopeStage.value = "input";
        }
        // Always clear ridge selection when changing modes
        clearRidgeSelection();
      }

      // Update draggable state of panels when switching to/from movePanel mode
      if (
        (currentMode.value === "movePanel" || mode === "movePanel") &&
        map.value &&
        map.value.overlays
      ) {
        map.value.overlays.forEach((overlay: any) => {
          if (overlay.panelId !== undefined) {
            overlay.setOptions({ draggable: mode === "movePanel" });
          }
        });
      }

      currentMode.value = mode;

      logDebug("Setting drawing mode to:", mode);

      if (drawingManager.value) {
        // Set cursor based on mode
        if (mode === "addPanel") {
          mapRef.value?.classList.add("panel-cursor");
          drawingManager.value.setDrawingMode(null); // Disable drawing
        } else if (mode === "movePanel") {
          mapRef.value?.classList.add("move-cursor");
          drawingManager.value.setDrawingMode(null); // Disable drawing
        } else {
          // Remove custom cursors
          mapRef.value?.classList.remove("panel-cursor", "move-cursor");
        }

        if (
          mode === "delete" ||
          mode === "slope" ||
          mode === "addPanel" ||
          mode === "movePanel"
        ) {
          drawingManager.value.setDrawingMode(null);
        } else if (mode === "roof") {
          drawingManager.value.setDrawingMode(
            google.maps.drawing.OverlayType.POLYGON
          );
          drawingManager.value.setOptions({
            polygonOptions: {
              fillColor: greenColor.value,
              fillOpacity: 0.3,
              strokeColor: greenColor.value,
              strokeWeight: 2,
              editable: true,
              clickable: true,
            },
          });
        } else if (mode === "obstacle") {
          drawingManager.value.setDrawingMode(
            google.maps.drawing.OverlayType.POLYGON
          );
          drawingManager.value.setOptions({
            polygonOptions: {
              fillColor: redColor.value,
              fillOpacity: 0.3,
              strokeColor: redColor.value,
              strokeWeight: 2,
              editable: true,
              clickable: true,
            },
          });
        } else {
          drawingManager.value.setDrawingMode(null);
        }
      }
    };

    // Helper function to calculate distance in meters between two points
    const calculateDistanceInMeters = (
      point1: { lat: number; lng: number },
      point2: { lat: number; lng: number }
    ): number => {
      // Debug log input points
      logDebug("Calculating distance between points:", { point1, point2 });

      // Validate input points
      if (
        !point1 ||
        !point2 ||
        typeof point1.lat !== "number" ||
        typeof point1.lng !== "number" ||
        typeof point2.lat !== "number" ||
        typeof point2.lng !== "number"
      ) {
        logDebug("Invalid input points:", { point1, point2 });
        return 0;
      }

      const lat1 = (point1.lat * Math.PI) / 180;
      const lat2 = (point2.lat * Math.PI) / 180;
      const deltaLat = ((point2.lat - point1.lat) * Math.PI) / 180;
      const deltaLng = ((point2.lng - point1.lng) * Math.PI) / 180;

      // Debug log intermediate calculations
      logDebug("Intermediate calculations:", {
        lat1,
        lat2,
        deltaLat,
        deltaLng,
      });

      const a =
        Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(lat1) *
          Math.cos(lat2) *
          Math.sin(deltaLng / 2) *
          Math.sin(deltaLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const earthRadius = 6371000; // Earth's radius in meters

      const distance = earthRadius * c;

      // Debug log final calculation
      logDebug("Distance calculation:", {
        a,
        c,
        distance,
      });

      return distance;
    };

    // Helper function to format coordinates
    const formatCoordinate = (coord: { lat: number; lng: number }): string => {
      return `${coord.lat.toFixed(6)}°, ${coord.lng.toFixed(6)}°`;
    };

    // Function to close screenshot info popup
    const closeScreenshotInfo = () => {
      showScreenshotInfo.value = false;
      screenshotInfo.value = null;
    };

    // Helper function to compress image
    const compressImage = (
      dataUrl: string,
      maxWidth: number = 800
    ): Promise<string> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            // Compress with lower quality
            resolve(canvas.toDataURL("image/jpeg", 0.7));
          } else {
            resolve(dataUrl);
          }
        };
        img.src = dataUrl;
      });
    };

    // Modified screenshot capture function
    const captureMapScreenshot = async () => {
      if (!mapRef.value || !map.value) return;

      try {
        logDebug("Capturing map screenshot");

        // Get map bounds
        const bounds = map.value.getBounds();
        if (!bounds) {
          logDebug("No bounds available from map");
          return;
        }

        // Get corner coordinates
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const nw = new google.maps.LatLng(ne.lat(), sw.lng());
        const se = new google.maps.LatLng(sw.lat(), ne.lng());

        // Debug log the coordinates
        logDebug("Map corner coordinates:", {
          northwest: { lat: nw.lat(), lng: nw.lng() },
          northeast: { lat: ne.lat(), lng: ne.lng() },
          southwest: { lat: sw.lat(), lng: sw.lng() },
          southeast: { lat: se.lat(), lng: se.lng() },
        });

        // Calculate dimensions in meters using Google Maps Geometry library
        const width = google.maps.geometry.spherical.computeDistanceBetween(
          nw,
          ne
        );
        const height = google.maps.geometry.spherical.computeDistanceBetween(
          nw,
          sw
        );

        logDebug("Calculated dimensions:", {
          width,
          height,
          widthIsNaN: isNaN(width),
          heightIsNaN: isNaN(height),
        });

        // Capture screenshot with higher quality
        const canvas = await html2canvas(mapRef.value, {
          useCORS: true,
          allowTaint: true,
          logging: false,
          scale: 4, // Increased scale for better quality
          backgroundColor: null,
          imageTimeout: 15000,
          width: mapRef.value.clientWidth,
          height: mapRef.value.clientHeight,
          scrollX: 0,
          scrollY: 0,
        });

        // Convert to high-quality PNG first
        const dataUrl = canvas.toDataURL("image/png", 1);

        // Compress the image
        const compressedDataUrl = await compressImage(dataUrl);

        // Create screenshot info object
        const info: ScreenshotInfo = {
          imageUrl: compressedDataUrl,
          dimensions: {
            width: width || 0,
            height: height || 0,
          },
          corners: {
            northwest: { lat: nw.lat(), lng: nw.lng() },
            northeast: { lat: ne.lat(), lng: ne.lng() },
            southwest: { lat: sw.lat(), lng: sw.lng() },
            southeast: { lat: se.lat(), lng: se.lng() },
          },
        };

        // Store in map state store
        mapStateStore.setScreenshotInfo(info);

        // Clear drawing data since we're taking a new screenshot
        projectStore.clearDrawingData();

        // Update project store with current map state
        projectStore.setMapState({
          center: mapStateStore.center,
          zoom: mapStateStore.zoom,

          screenshot_info: info,
        });

        // Update local state
        screenshotInfo.value = info;
        showScreenshotInfo.value = true;

        logDebug("Map screenshot captured with additional information");
      } catch (error) {
        console.error("Error capturing map screenshot:", error);
      }
    };

    onMounted(async () => {
      try {
        await loadGoogleMapsScript();
        // Add a small delay to ensure DOM is ready
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Check if projectStore has valid mapState
        if (projectStore.project?.map_state) {
          const { center, zoom } = projectStore.project.map_state;
          // Check if center exists and has valid coordinates (not 0,0)
          if (center && center.lat !== 0 && center.lng !== 0) {
            mapStateStore.updateMapState(center, zoom);
            logDebug("Using map state from project store:", { center, zoom });
          }
        }

        if (mapRef.value) {
          initMap();
          logDebug("Map initialized successfully");
        } else {
          logDebug("Map container not ready");
        }
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    });

    onUnmounted(() => {
      // Clean up event listeners
      if (map.value) {
        google.maps.event.clearInstanceListeners(map.value);
      }
      logDebug("Map component unmounted, state preserved");
    });

    return {
      mapRef,
      searchInput,
      currentMode,
      setDrawingMode,
      clearSearch,
      areaInfo,
      findMyLocation,
      clearMap,
      areas,
      panels,
      showSlopePopup,
      slopeValue,
      directionValue,
      slopeStage,
      isValidSlope,
      isValidDirection,
      getDirectionName,
      cancelSlope,
      applySlope,
      captureMapScreenshot,
      showScreenshotNotification,
      showScreenshotInfo,
      screenshotInfo,
      closeScreenshotInfo,
      formatCoordinate,
      isLocating,
    };
  },
});
/* eslint-enable */
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 85vh;
  position: relative;
  border: 1px solid var(--color-dark);
  border-radius: 2px;
  box-sizing: border-box;
}

.map-area {
  width: 100%;
  height: 100%;
}

.search-box {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: 400px;
}

.search-box .input-group {
  background: var(--color-white);
  border-radius: 2px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  width: 100%;
}

.search-box input {
  border: none;
  height: 40px;
  padding: 8px 12px;
  border-radius: 2px 0 0 2px;
}

.search-box input:focus {
  box-shadow: none;
  border-color: var(--primary-color);
}

.search-input::placeholder {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23A6A6A6' viewBox='0 0 16 16'%3E%3Cpath d='M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z'/%3E%3C/svg%3E ");
  background-repeat: no-repeat;
  background-position: 0 center;
  background-size: 16px 16px;
  padding-left: 20px;
}

.search-box button {
  border: none;
  background: var(--color-white);
  color: var(--color-gray);
  padding: 8px 12px;
  border-radius: 0 2px 2px 0;
}

.search-box button:hover {
  background: var(--color-light-light-gray);
  color: var(--color-dark-dark);
}

.drawing-controls {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.control-button {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--color-white);
  border: 1px solid var(--color-light-gray);
  border-radius: 2px;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease-in-out;
}

.control-button:hover {
  background-color: var(--color-light-light-gray);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.control-button:active {
  transform: translateY(0);
  background-color: var(--color-light-gray);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.control-button.active {
  background-color: var(--color-light);
  border-color: var(--primary-color);
}

.control-button.active:hover {
  background-color: var(--color-light);
}

.control-button i {
  font-size: 24px;
  color: var(--color-dark-dark);
  transition: color 0.2s ease-in-out;
}

.control-button:hover i {
  color: var(--primary-color);
}

.control-button.active i {
  color: var(--secondary-color);
}

.slope-popup {
  position: absolute;
  top: 10px;
  left: 10px;
  transform: none;
  z-index: 2000;
  width: 350px;
}

.slope-popup-content {
  background-color: var(--color-white);
  border-radius: 2px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 16px;
}

.direction-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
}

.compass-rose {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 2px;
  border: 2px solid var(--color-light-gray);
  display: flex;
  align-items: center;
  justify-content: center;
}

.compass-arrow {
  width: 4px;
  height: 70px;
  background: linear-gradient(
    to bottom,
    var(--primary-color) 0%,
    var(--primary-color) 50%,
    var(--color-red) 50%,
    var(--color-red) 100%
  );
  transform-origin: center;
  border-radius: 2px;
}

.compass-label {
  position: absolute;
  font-weight: bold;
  font-size: 12px;
}

.compass-label.north {
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
}

.compass-label.east {
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
}

.compass-label.south {
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%);
}

.compass-label.west {
  left: 5px;
  top: 50%;
  transform: translateY(-50%);
}

.direction-value {
  flex: 1;
}

.screenshot-notification {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2000;
  transition: opacity 0.3s ease-in-out;
  animation: fadeInOut 3s ease-in-out;
}

.screenshot-notification .alert {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border: none;
  padding: 12px 20px;
  border-radius: 2px;
  font-weight: 500;
}

/* Custom cursors for panel modes */
.panel-cursor {
  cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="%232196F3" stroke="%23ffffff" stroke-width="2"><rect x="2" y="5" width="10" height="14" rx="1" /></svg>')
      5 5,
    auto;
}

.move-cursor {
  cursor: move;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.screenshot-info-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  width: 100%;
  height: 100%;
}

.screenshot-info-content {
  background-color: var(--color-white);
  border-radius: 2px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  width: 40%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 20px;
  margin: 20px;
  position: relative;
}

.screenshot-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.screenshot-header h6 {
  margin: 0;
  color: var(--color-dark-dark);
}

.screenshot-image-container {
  margin-bottom: 10px;
  text-align: center;
}

.screenshot-image-container img {
  max-width: 100%;
  height: auto;
  border-radius: 2px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.screenshot-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.detail-section {
  background-color: var(--color-light-light-gray);
  padding: 10px;
  border-radius: 2px;
}

.detail-section h7 {
  margin-bottom: 10px;
  color: var(--primary-color);
}

.coordinates-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.coordinate-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.coordinate-item .label {
  font-weight: 500;
  color: var(--color-gray);
}

.coordinate-item .value {
  font-family: monospace;
  color: var(--color-dark-dark);
}

@media (max-width: 768px) {
  .screenshot-details {
    grid-template-columns: 1fr;
  }
}

.hidden-button {
  display: none;
}

.location-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 2px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.loading-text {
  color: var(--color-dark-dark);
  font-size: 14px;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}
</style>
